import React, { createContext, useCallback, useContext, useMemo, useState, useRef } from 'react';
import { Form, message } from 'antd';

const FormContext = createContext();

const NO_TOUCHED_FIELDS = Object.freeze({});

export const useFormContext = () => {
    const context = useContext(FormContext);
    if (context === undefined) {
        throw new Error('useFormContext must be used within a FormProvider');
    }
    return context;
}

export const useOptionalFormContext = () => {
    const context = useContext(FormContext);
    return context ?? null;
}

export const FormProvider = ({ children }) => {
    const [form] = Form.useForm();
    const [touchedFields, setTouchedFields] = useState(NO_TOUCHED_FIELDS);
    const [customCancelMessage, setCustomCancelMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Refs to track async submit promise resolution
    const submitResolveRef = useRef(null);
    const submitRejectRef = useRef(null);
    const isSubmittingRef = useRef(false);

    const settlePendingSubmit = useCallback((error) => {
        const resolve = submitResolveRef.current;
        const reject = submitRejectRef.current;
        submitResolveRef.current = null;
        submitRejectRef.current = null;
        if (error) {
            reject?.(error);
        } else {
            resolve?.();
        }
    }, []);

    const onFinish = useCallback((userFinish) => async (...args) => {
        // Submit buttons stay enabled while a save is in flight (they are gated on
        // "is anything touched?", which only clears on success), so guard against a
        // second click firing a duplicate save.
        if (isSubmittingRef.current) {
            settlePendingSubmit(new Error('A save is already in progress'));
            return;
        }
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        try {
            await userFinish(...args);
            setTouchedFields(NO_TOUCHED_FIELDS);
            settlePendingSubmit();
        } catch (error) {
            settlePendingSubmit(error ?? new Error('Form submission failed'));
            if (error && error.errorFields) {
                return;
            }
            console.error("Error during form submission:", error);
            message.error('Failed to save changes. Please try again.');
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    }, [settlePendingSubmit]);

    // Async submit method that can be awaited by Panel.
    // Validation runs first so an invalid form rejects immediately: antd only calls
    // onFinish when validation passes, so registering the promise before validating
    // would leave the caller awaiting a promise that can never settle.
    const submitFormAsync = useCallback(async () => {
        await form.validateFields();
        settlePendingSubmit(new Error('Form submission superseded'));
        return new Promise((resolve, reject) => {
            submitResolveRef.current = resolve;
            submitRejectRef.current = reject;
            form.submit();
        });
    }, [form, settlePendingSubmit]);

    const markFieldAsTouched = useCallback((fieldName) => {
        setTouchedFields((prev) => (prev[fieldName] ? prev : { ...prev, [fieldName]: true }));
    }, []);

    const isAnyFieldTouched = useCallback(
        () => Object.keys(touchedFields).length > 0,
        [touchedFields]
    );

    const clearTouchedFields = useCallback((fieldName) => {
        if (!fieldName) {
            setTouchedFields(NO_TOUCHED_FIELDS);
            return;
        }
        setTouchedFields((prev) => {
            if (!(fieldName in prev)) {
                return prev;
            }
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });
    }, []);

    const setSaveChangesMessage = useCallback((comment) => {
        setCustomCancelMessage(comment);
    }, []);

    const value = useMemo(
        () => ({
            form,
            onFinish,
            submitFormAsync,
            isSubmitting,
            touchedFields,
            clearTouchedFields,
            markFieldAsTouched,
            isAnyFieldTouched,
            customCancelMessage,
            setSaveChangesMessage,
        }),
        [
            form,
            onFinish,
            submitFormAsync,
            isSubmitting,
            touchedFields,
            clearTouchedFields,
            markFieldAsTouched,
            isAnyFieldTouched,
            customCancelMessage,
            setSaveChangesMessage,
        ]
    );

    return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};